import type { Ref } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import type { EnrichedComparisonSheet } from "@/payroll/editor/types";
import { ComparisonGridView } from "./comparison-grid";
import { FormulaBar } from "./formula-bar";

/** Which payroll you are looking at, over each half of the split. */
const HEADING =
  "shrink-0 border-b border-line bg-surface-sunken px-3 py-1.5 text-xs leading-[1.45] font-medium tracking-widest text-subtle uppercase";

/**
 * Today's payroll beside the proposal, one sheet each.
 *
 * Both halves take the same marks, the same selection and the same row and
 * column counts, because the point of a comparator is that a cell is in the
 * same place on both sides. The two viewports are mirrored by the caller.
 */
export const ComparisonSides = ({
  sheet,
  address,
  inspecting,
  onInspect,
  describe,
  currentRef,
  proposedRef,
}: {
  sheet: EnrichedComparisonSheet;
  address: string | null;
  inspecting: string | null;
  onInspect: (coord: string | null) => void;
  describe: (row: number, col: number) => string | undefined;
  currentRef: Ref<HTMLDivElement>;
  proposedRef: Ref<HTMLDivElement>;
}) => (
  <div className="grid min-h-0 flex-1 grid-cols-2 divide-x divide-line">
    <Stack
      gap={0}
      align="stretch"
      direction="column"
      className="flex min-h-0 min-w-0 flex-col"
    >
      <p className={HEADING}>Today&apos;s rules</p>
      <FormulaBar
        address={address}
        value={inspecting ? sheet.sides.current.get(inspecting) : undefined}
      />
      <ComparisonGridView
        viewportRef={currentRef}
        describe={describe}
        selected={inspecting}
        onSelect={onInspect}
        grid={sheet.current}
        marks={sheet.marks}
        rows={sheet.rows}
        cols={sheet.cols}
        emptyLabel="This sheet does not exist in today's payroll."
      />
    </Stack>
    <Stack
      gap={0}
      align="stretch"
      direction="column"
      className="flex min-h-0 min-w-0 flex-col"
    >
      <p className={HEADING}>Your proposal</p>
      <FormulaBar
        address={address}
        value={inspecting ? sheet.sides.proposed.get(inspecting) : undefined}
      />
      <ComparisonGridView
        viewportRef={proposedRef}
        describe={describe}
        selected={inspecting}
        onSelect={onInspect}
        grid={sheet.proposed}
        marks={sheet.marks}
        rows={sheet.rows}
        cols={sheet.cols}
        emptyLabel="This sheet is not produced by your proposal."
      />
    </Stack>
  </div>
);
